import React, { useState, useEffect, useRef } from 'react';
import { createChart } from 'lightweight-charts';
import { FaTimes, FaSpinner } from 'react-icons/fa';
import { BsBarChart, BsGraphUp, BsThreeDots } from 'react-icons/bs';
import { getHistoricalChartData, getQuoteData } from '../api/dataApi';
import DrawingToolbar from './DrawingToolbar';

const StockChartModal = ({ isOpen, onClose, symbol, companyName = null }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [interval, setInterval] = useState('15m'); // Candle interval
  const [chartType, setChartType] = useState('candlestick'); // 'candlestick' or 'line'
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const [latestDayData, setLatestDayData] = useState(null); // Latest daily OHLC for legend
  const [hoverData, setHoverData] = useState(null); // Hovered candle OHLC
  const [activeTool, setActiveTool] = useState('crosshair'); // Active drawing tool
  const chartContainerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Quick access intervals (TradingView-style)
  const quickIntervals = ['1d', '15m', '1h', '4h', '1w', '1mo'];

  // All interval options for the "More" dropdown
  const allIntervalOptions = [
    { value: '1m', label: '1 Minute' },
    { value: '3m', label: '3 Minutes' },
    { value: '5m', label: '5 Minutes' },
    { value: '10m', label: '10 Minutes' },
    { value: '15m', label: '15 Minutes' },
    { value: '30m', label: '30 Minutes' },
    { value: '45m', label: '45 Minutes' },
    { value: '1h', label: '1 Hour' },
    { value: '2h', label: '2 Hours' },
    { value: '3h', label: '3 Hours' },
    { value: '4h', label: '4 Hours' },
    { value: '1d', label: '1 Day' },
    { value: '1w', label: '1 Week' },
    { value: '1mo', label: '1 Month' },
  ];

  // Get display label for current interval
  const getIntervalLabel = (intervalValue = interval) => {
    const option = allIntervalOptions.find(opt => opt.value === intervalValue);
    return option ? option.label : intervalValue.toUpperCase();
  };

  // Get short label for quick buttons
  const getShortLabel = (intervalValue) => {
    const labels = {
      '1d': '1D',
      '15m': '15m',
      '1h': '1H',
      '4h': '4H',
      '1w': '1W',
      '1mo': '1M',
    };
    return labels[intervalValue] || intervalValue.toUpperCase();
  };

  // Handle interval selection
  const handleIntervalSelect = (value) => {
    setInterval(value);
    setIsMoreDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsMoreDropdownOpen(false);
      }
    };

    if (isMoreDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMoreDropdownOpen]);

  // Single useEffect for complete chart lifecycle
  useEffect(() => {
    // Exit early if modal is not open or refs not ready
    if (!isOpen || !chartContainerRef.current || !symbol) {
      return;
    }

    console.log(`--- DEBUG: Starting chart initialization for ${symbol} with ${interval} interval ---`);

    // Local chart instance variable (scoped to this effect)
    let chart = null;
    let series = null;

    const handleResize = () => {
      if (chart && chartContainerRef.current) {
        const newWidth = chartContainerRef.current.clientWidth;
        const newHeight = Math.max(chartContainerRef.current.clientHeight - 100, 450);
        chart.applyOptions({ 
          width: newWidth,
          height: newHeight 
        });
        console.log(`📐 Chart resized to ${newWidth}x${newHeight}px`);
      }
    };

    const initializeChart = async () => {
      try {
        setLoading(true);
        setError(null);

        // STEP 1: Fetch latest daily quote data for OHLC legend
        try {
          const quoteResponse = await getQuoteData(symbol);
          if (quoteResponse.success) {
            setLatestDayData({
              open: quoteResponse.open,
              high: quoteResponse.high,
              low: quoteResponse.low,
              close: quoteResponse.close,
            });
            console.log('✅ Latest quote data fetched for legend');
          }
        } catch (quoteError) {
          console.warn('⚠️ Could not fetch latest quote:', quoteError);
          // Continue anyway - chart can still work without legend data
        }

        // STEP 2: Clear container to ensure clean slate
        chartContainerRef.current.innerHTML = '';
        console.log('🧹 Container cleared');

        // STEP 3: Get container dimensions
        // Reduce height by 100px to move X-axis up from bottom edge as requested
        const containerWidth = chartContainerRef.current.clientWidth || 800;
        const containerHeight = Math.max((chartContainerRef.current.clientHeight || 600) - 100, 450);
        console.log(`📐 Chart dimensions: ${containerWidth}x${containerHeight}`);

        // STEP 4: Create chart instance (TradingView-style white theme)
        chart = createChart(chartContainerRef.current, {
          width: containerWidth,
          height: containerHeight,
          layout: {
            backgroundColor: '#FFFFFF',
            textColor: '#374151',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          },
          autoSize: false,  // Manual sizing for precise control
          grid: {
            vertLines: { color: '#E5E7EB', style: 1 },
            horzLines: { color: '#E5E7EB', style: 1 },
          },
          crosshair: {
            mode: 1,  // Normal crosshair mode
            vertLine: {
              width: 1,
              color: '#9CA3AF',
              style: 3,
              labelBackgroundColor: '#1F2937',
            },
            horzLine: {
              width: 1,
              color: '#9CA3AF',
              style: 3,
              labelBackgroundColor: '#1F2937',
            },
          },
          rightPriceScale: {
            borderColor: '#E5E7EB',
            visible: true,
            scaleMargins: {
              top: 0.1,
              bottom: 0.1,
            },
            entireTextOnly: false,
          },
          timeScale: {
            borderColor: '#E5E7EB',
            borderVisible: true,
            visible: true,           // CRITICAL: Show time scale
            timeVisible: true,       // Show time on X-axis labels
            secondsVisible: false,   // Hide seconds for clarity
            rightOffset: 0,          // No padding on right - Y-axis touches edge
            barSpacing: 6,           // Space between candles
            fixLeftEdge: false,
            fixRightEdge: false,
            lockVisibleTimeRangeOnResize: false,
            rightBarStaysOnScroll: false,
            shiftVisibleRangeOnNewBar: true,
            timeZone: 'Asia/Kolkata',  // CRITICAL: Use IST timezone for proper candle alignment
          },
          localization: {
            // Format for date/time display in Kolkata timezone (IST)
            timeFormatter: (timestamp) => {
              const date = new Date(timestamp * 1000);
              // Dynamic formatting based on interval
              if (interval.includes('m') || interval.includes('h')) {
                // For intraday: show time with date in Kolkata timezone
                return date.toLocaleString('en-IN', {
                  timeZone: 'Asia/Kolkata',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                });
              } else {
                // For daily+: show date with year in Kolkata timezone
                return date.toLocaleString('en-IN', {
                  timeZone: 'Asia/Kolkata',
                  month: 'short',
                  day: 'numeric',
                  year: '2-digit'
                });
              }
            },
          },
        });

        console.log('✅ Chart instance created:', typeof chart);
        console.log('Chart methods available:', Object.getOwnPropertyNames(Object.getPrototypeOf(chart)));

        // STEP 5: Add series IMMEDIATELY (before async operations)
        // V4 API: Direct addCandlestickSeries / addLineSeries methods
        if (chartType === 'candlestick') {
          console.log('📊 Adding candlestick series using V4 API...');
          
          series = chart.addCandlestickSeries({
            upColor: '#10B981',      // Green for bullish
            downColor: '#EF4444',    // Red for bearish
            borderUpColor: '#10B981',
            borderDownColor: '#EF4444',
            wickUpColor: '#10B981',
            wickDownColor: '#EF4444',
          });
        } else {
          console.log('📈 Adding line series using V4 API...');
          
          series = chart.addLineSeries({
            color: '#3B82F6',
            lineWidth: 2,
          });
        }

        console.log('✅ Series added successfully:', typeof series);

        // STEP 6: Fetch data from backend (interval controls candle size)
        console.log(`📡 Fetching chart data for ${symbol} with ${interval} interval...`);
        const response = await getHistoricalChartData(symbol, interval);

        if (!response.success || !response.data || response.data.length === 0) {
          throw new Error('No chart data available for this symbol');
        }

        console.log(`✅ Received ${response.data_points} data points`);

        // STEP 7: Set data on the series
        if (chartType === 'candlestick') {
          series.setData(response.data);
        } else {
          // For line chart, only use time and close
          const lineData = response.data.map(d => ({
            time: d.time,
            value: d.close
          }));
          series.setData(lineData);
        }

        console.log('✅ Data set on series');

        // STEP 8: Fit content to view
        chart.timeScale().fitContent();

        // STEP 8.5: Force immediate resize to ensure axes touch borders and X-axis is visible
        setTimeout(() => {
          if (chart && chartContainerRef.current) {
            const width = chartContainerRef.current.clientWidth;
            const height = Math.max(chartContainerRef.current.clientHeight - 100, 450);
            chart.applyOptions({ width, height });
            chart.timeScale().fitContent();
            console.log('🔄 Applied immediate resize for proper axis positioning');
          }
        }, 100);

        // STEP 9: Add crosshair move handler to update floating OHLC legend
        chart.subscribeCrosshairMove((param) => {
          if (!param.time || !param.point) {
            setHoverData(null);
            return;
          }

          const data = param.seriesData.get(series);
          if (data) {
            // Update hover data for floating legend display
            if (chartType === 'candlestick') {
              setHoverData({
                open: data.open,
                high: data.high,
                low: data.low,
                close: data.close,
                time: param.time,
              });
            } else {
              // For line chart, only close/value is available
              setHoverData({
                value: data.value,
                time: param.time,
              });
            }

            // Format the tooltip data in Kolkata timezone
            const date = new Date(param.time * 1000);
            let dateStr;
            
            if (interval.includes('m') || interval.includes('h')) {
              // Intraday: show full date and time in Kolkata timezone
              dateStr = date.toLocaleString('en-IN', {
                timeZone: 'Asia/Kolkata',
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              });
            } else {
              // Daily: show date with year in Kolkata timezone
              dateStr = date.toLocaleString('en-IN', {
                timeZone: 'Asia/Kolkata',
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              });
            }

            console.log(`🎯 Crosshair (IST): ${dateStr} | O:${data.open} H:${data.high} L:${data.low} C:${data.close || data.value}`);
          }
        });

        // STEP 10: Add resize listener
        window.addEventListener('resize', handleResize);

        console.log('✅ Chart rendered successfully');

      } catch (err) {
        console.error('❌ Error in chart initialization:', err);
        console.error('Error stack:', err.stack);
        setError(err.response?.data?.detail || err.message || 'Failed to load chart data');
      } finally {
        setLoading(false);
      }
    };

    // Start the initialization
    initializeChart();

    // Cleanup function - runs when dependencies change or component unmounts
    return () => {
      console.log('--- DEBUG: Cleanup function running ---');
      window.removeEventListener('resize', handleResize);
      
      if (chart) {
        try {
          console.log('🧹 Removing chart instance');
          chart.remove();
          chart = null;
        } catch (e) {
          console.warn('⚠️ Error removing chart:', e);
        }
      }
    };
  }, [isOpen, symbol, interval, chartType]); // Re-run when any of these change

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
              <span>{companyName || symbol}</span>
              <span className="text-base font-normal text-gray-500">• {getIntervalLabel()}</span>
              <span className="text-base font-normal text-gray-500">• NSE</span>
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          {/* Unified Control Toolbar */}
          <div className="flex items-center justify-between py-3 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              {/* Quick Interval Buttons */}
              <div className="flex items-center space-x-1">
                {quickIntervals.map((intervalValue) => (
                  <button
                    key={intervalValue}
                    onClick={() => handleIntervalSelect(intervalValue)}
                    className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                      interval === intervalValue
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {getShortLabel(intervalValue)}
                  </button>
                ))}
                
                {/* More Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsMoreDropdownOpen(!isMoreDropdownOpen)}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded transition-colors flex items-center space-x-1"
                  >
                    <BsThreeDots className="text-lg" />
                  </button>
                  
                  {isMoreDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50 w-48 max-h-80 overflow-y-auto">
                      {allIntervalOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleIntervalSelect(option.value)}
                          className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                            interval === option.value
                              ? 'bg-blue-600 text-white font-medium'
                              : 'text-gray-800 hover:bg-gray-100'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Divider */}
              <div className="h-6 w-px bg-gray-300"></div>
              
              {/* Chart Type Icon Buttons */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setChartType('candlestick')}
                  className={`p-2 rounded transition-colors ${
                    chartType === 'candlestick'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  title="Candlestick Chart"
                >
                  <BsBarChart className="text-lg" />
                </button>
                <button
                  onClick={() => setChartType('line')}
                  className={`p-2 rounded transition-colors ${
                    chartType === 'line'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  title="Line Chart"
                >
                  <BsGraphUp className="text-lg" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Chart Content - Flex Grow Container with proper overflow */}
        <div className="flex-grow bg-gray-50 flex flex-col min-h-0">
          {error && (
            <div className="mx-6 mt-4 mb-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 flex-shrink-0">
              <p className="font-semibold mb-1">❌ Error Loading Chart</p>
              <p className="text-sm">{error}</p>
            </div>
          )}

          {loading && (
            <div className="flex flex-col items-center justify-center flex-grow">
              <FaSpinner className="animate-spin text-6xl text-blue-600 mb-4" />
              <p className="text-gray-700 text-lg">Loading chart data...</p>
              <p className="text-gray-500 text-sm mt-2">Fetching {getIntervalLabel()} data</p>
            </div>
          )}

          {/* Chart Container with Overlays - Axes render on top of canvas */}
          <div className={`relative flex-grow ${loading ? 'hidden' : 'flex'} min-h-0 pb-24`}>
            {/* Chart Canvas - Reduced height to ensure X-axis labels are fully visible */}
            <div 
              ref={chartContainerRef}
              className="w-full h-full"
              style={{ position: 'relative' }}
            />
            
            {/* OHLC Data Legend (Top-Left Overlay, positioned after toolbar) */}
            {!loading && (hoverData || latestDayData) && (
              <div className="absolute top-3 left-16 bg-white bg-opacity-95 rounded-lg shadow-md px-4 py-2 z-20 border border-gray-200">
                <div className="flex items-center space-x-4 text-sm font-medium">
                  {chartType === 'candlestick' && (hoverData || latestDayData) && (
                    <>
                      <span className="text-gray-600">
                        O <span className="text-gray-900">{(hoverData?.open || latestDayData?.open)?.toFixed(2)}</span>
                      </span>
                      <span className="text-gray-600">
                        H <span className="text-gray-900">{(hoverData?.high || latestDayData?.high)?.toFixed(2)}</span>
                      </span>
                      <span className="text-gray-600">
                        L <span className="text-gray-900">{(hoverData?.low || latestDayData?.low)?.toFixed(2)}</span>
                      </span>
                      <span className="text-gray-600">
                        C <span className={
                          (hoverData?.close || latestDayData?.close) >= (hoverData?.open || latestDayData?.open)
                            ? 'text-green-600'
                            : 'text-red-600'
                        }>
                          {(hoverData?.close || latestDayData?.close)?.toFixed(2)}
                        </span>
                      </span>
                    </>
                  )}
                  {chartType === 'line' && (hoverData || latestDayData) && (
                    <span className="text-gray-600">
                      Value <span className="text-gray-900">{(hoverData?.value || hoverData?.close || latestDayData?.close)?.toFixed(2)}</span>
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Drawing Toolbar (Left Side Overlay) */}
            {!loading && (
              <DrawingToolbar 
                activeTool={activeTool}
                setActiveTool={setActiveTool}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-200 px-6 py-3 bg-white">
          <div className="flex items-center justify-center space-x-8 text-xs text-gray-600">
            <div className="flex items-center space-x-1">
              <span className="font-semibold text-gray-700">X-Axis:</span>
              <span>{interval.includes('m') || interval.includes('h') ? 'Time & Date (IST)' : 'Date (IST)'}</span>
            </div>
            <div className="flex items-center space-x-1">
              <span className="font-semibold text-gray-700">Y-Axis:</span>
              <span>Price (₹)</span>
            </div>
            <div className="flex items-center space-x-1">
              <span className="font-semibold text-gray-700">Tip:</span>
              <span>Scroll to zoom • Drag to pan • Hover for OHLC</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockChartModal;

